// Dashboard: plan summary + quick-action cards, plus two conversion-pipeline
// (W1) touches — Q2 post-checkout success modal and Q3 empty-state CTA for
// brand-new users who haven't designed a badge yet.
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser, useUsage, useSendTestBadge, useEngagement } from '../hooks/useApi';
import { ConversionSurvey } from '../components/ConversionSurvey';
import { PLAN_DETAILS } from '../types';

export function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { data: user, isLoading } = useUser();
  const { data: usage } = useUsage();
  const { data: engagement } = useEngagement();
  const { mutate: sendTestBadge, isPending: testBadgePending } = useSendTestBadge();
  const [testBadgeResult, setTestBadgeResult] = useState<{ url?: string; msg: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutDismissed, setCheckoutDismissed] = useState(false);

  // Q2 — Stripe redirects to /dashboard?checkout=success on a successful
  // upgrade (see BWS-Stripe_checkout success_url). Pop a one-time modal.
  const showCheckoutSuccess = searchParams.get('checkout') === 'success' && !checkoutDismissed;

  if (isLoading) {
    return <div className="p-8 text-gray-500">{t('loading')}</div>;
  }

  const plan = user?.plan ?? 'free';
  const planDetails = PLAN_DETAILS[plan];

  // D2 — monthly award usage meter + near-limit upgrade nudge. Shown once the
  // user is actually issuing; the upgrade nudge appears at >=80% for plans that
  // have a next tier (free/mid). Suppressed before the first issue (Q3 covers new users).
  const awardsUsed = usage?.usage.awardsThisYear ?? 0;
  const awardsCap = usage?.limits.awardsPerYear ?? 0;
  const usagePct = awardsCap > 0 ? Math.min(100, Math.round((awardsUsed / awardsCap) * 100)) : 0;

  // D1 — guided onboarding. "Has really issued" keys on usage.activatedAt (set
  // ONLY by the real issue path), NOT awardsThisYear: the auto-seed sweep creates
  // a [Sample] self-award ~48h after signup, which would otherwise flip a count
  // and silently hide this checklist (and light up the meter) for a user who
  // never issued anything real — the exact masking the 2026-10 activation audit
  // found. `starterKitSeeded` tells us the sample issuer+badge already exist, so
  // we skip the "send a test badge" offer and point straight at a real issue.
  const hasIssued = (usage?.activatedAt ?? null) != null;
  const starterKitSeeded = !!usage?.starterKitSeeded;
  const showGettingStarted = usage != null && !hasIssued;
  const stepIssuer = (usage?.usage.issuers ?? 0) > 0;
  const stepBadge = (usage?.usage.badges ?? 0) > 0;

  // Suppress the meter until a REAL issue — a lone seeded sample award shouldn't
  // read as "1 used" progress on a brand-new account.
  const showMeter = usage != null && hasIssued && awardsCap > 0;
  const nearLimit = showMeter && plan !== 'top' && usagePct >= 80;
  const meterTone = usagePct >= 100 ? 'bg-red-500' : usagePct >= 80 ? 'bg-amber-500' : 'bg-primary-600';
  // The test badge seeds one issuer + one badge. Don't offer it once the user
  // is at either cap — seeding would exceed the plan limit (and the backend
  // now declines it), so hide the CTA rather than show a button that fails.
  // limits.issuers/badges is null for unlimited (Pro) → treat as Infinity so
  // Pro users are never considered "at limit".
  const atSeedLimit = usage != null && (
    (usage.usage.issuers ?? 0) >= (usage.limits.issuers ?? Infinity) ||
    (usage.usage.badges ?? 0) >= (usage.limits.badges ?? Infinity)
  );

  // D5 — show the engagement widget once there's any recipient engagement.
  const showEngagement = engagement != null && engagement.awardsWithEngagement > 0;

  const handleTestBadge = () => {
    setTestBadgeResult(null);
    sendTestBadge(undefined, {
      onSuccess: (res) => setTestBadgeResult(
        res.ok
          ? { url: res.awardViewUrl, msg: t('onboarding.testBadgeDone') }
          : { msg: t('onboarding.testBadgeAlready') }
      ),
      onError: () => setTestBadgeResult({ msg: t('onboarding.testBadgeError') })
    });
  };

  const dismissCheckout = () => {
    setCheckoutDismissed(true);
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="page-wide space-y-8 p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('welcome', { name: user?.name || user?.email || '' })}
          </h1>
          <p className="mt-2 text-gray-600">{t('subtitle')}</p>
        </div>
        {/* UX-1 — persistent plan pill above the fold (was only a card further down),
            so a paid customer always sees their plan is active. Links to pricing. */}
        <Link
          to="/pricing"
          title={t('usage.upgrade')}
          className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold transition hover:opacity-90 ${
            plan === 'top'
              ? 'bg-primary-100 text-primary-700'
              : plan === 'mid'
                ? 'bg-indigo-100 text-indigo-700'
                : 'bg-gray-100 text-gray-600'
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
          {planDetails.name}
        </Link>
      </div>

      {showMeter && (
        <div className={`rounded-lg border p-5 ${nearLimit ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-medium text-gray-700">{t('usage.title')}</p>
            <p className="text-sm text-gray-500">{t('usage.count', { used: awardsUsed, cap: awardsCap })}</p>
          </div>
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div className={`h-full rounded-full ${meterTone}`} style={{ width: `${usagePct}%` }} />
          </div>
          {nearLimit && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-amber-800">
                {usagePct >= 100 ? t('usage.atLimit') : t('usage.nearLimit', { pct: usagePct })}
              </p>
              <Link to="/pricing" className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
                {t('usage.upgrade')}
              </Link>
            </div>
          )}
        </div>
      )}

      {showGettingStarted && (
        <div className="rounded-lg border-2 border-dashed border-primary-300 bg-primary-50 p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-primary-900">
            {starterKitSeeded ? t('onboarding.seededTitle', 'One step left — issue a real credential') : t('onboarding.title')}
          </h2>
          <p className="mt-1 text-sm text-primary-800">
            {starterKitSeeded
              ? t('onboarding.seededSubtitle', 'We set up a sample issuer and badge for you. Edit the sample (or design your own), then award it to a real recipient to go live.')
              : t('onboarding.subtitle')}
          </p>
          <ol className="mt-4 space-y-2">
            {[
              { done: stepIssuer, label: t('onboarding.step1'), to: '/issuers' },
              { done: stepBadge, label: t('onboarding.step2'), to: '/badges' },
              { done: hasIssued, label: t('onboarding.step3'), to: '/badges' }
            ].map((s, i) => (
              <li key={i} className="flex items-center gap-3">
                <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${s.done ? 'bg-green-600 text-white' : 'bg-white text-primary-700 border border-primary-300'}`}>
                  {s.done ? '✓' : i + 1}
                </span>
                <Link to={s.to} className={`text-sm ${s.done ? 'text-gray-400 line-through' : 'text-primary-900 hover:underline'}`}>{s.label}</Link>
              </li>
            ))}
          </ol>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {starterKitSeeded ? (
              // Sample kit already exists — don't offer to seed again; drive the
              // one action that actually activates the account.
              <Link
                to="/badges"
                className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
              >
                {t('onboarding.issueRealCta', 'Award to a real recipient')}
              </Link>
            ) : (
              <>
                {!atSeedLimit && (
                  <button
                    type="button"
                    onClick={handleTestBadge}
                    disabled={testBadgePending}
                    className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
                  >
                    {testBadgePending ? t('onboarding.testBadgeSending') : t('onboarding.testBadgeCta')}
                  </button>
                )}
                <Link to="/badges" className="text-sm font-medium text-primary-700 hover:underline">{t('onboarding.orDesign')}</Link>
              </>
            )}
          </div>
          {testBadgeResult && (
            <p className="mt-3 text-sm text-primary-900">
              {testBadgeResult.msg}{' '}
              {testBadgeResult.url && <a href={testBadgeResult.url} target="_blank" rel="noreferrer" className="font-semibold underline">{t('onboarding.viewIt')}</a>}
            </p>
          )}
          <ConversionSurvey />
        </div>
      )}

      {showEngagement && (
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-medium text-gray-700">{t('engagement.title')}</p>
            <span className="text-xs text-gray-400">{t('engagement.acrossAwards', { count: engagement!.awardsWithEngagement })}</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { n: engagement!.totals.views, l: t('engagement.views') },
              { n: engagement!.totals.shares, l: t('engagement.shares') },
              { n: engagement!.totals.downloads, l: t('engagement.downloads') },
              { n: engagement!.totals.linkedinAdds, l: t('engagement.linkedin') }
            ].map((s, i) => (
              <div key={i} className="rounded-lg bg-gray-50 p-3 text-center">
                <p className="text-2xl font-bold text-gray-900">{s.n.toLocaleString()}</p>
                <p className="mt-0.5 text-xs text-gray-500">{s.l}</p>
              </div>
            ))}
          </div>
          {plan === 'free' && (
            <p className="mt-3 text-xs text-gray-400">{t('engagement.upsell')}</p>
          )}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{t('currentPlan')}</p>
            <p className="mt-1 text-2xl font-semibold">{planDetails.name}</p>
            <p className="mt-1 text-sm text-gray-600">{t(`pricing:plans.${plan}.description`)}</p>
          </div>
          <Link
            to="/pricing"
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            {t('managePlan')}
          </Link>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Link to="/api-keys" className="rounded-lg border border-gray-200 bg-white p-6 hover:border-gray-300">
          <h3 className="text-lg font-semibold">{t('apiKeys.title')}</h3>
          <p className="mt-2 text-sm text-gray-600">{t('apiKeys.description')}</p>
        </Link>
        <Link to="/profile" className="rounded-lg border border-gray-200 bg-white p-6 hover:border-gray-300">
          <h3 className="text-lg font-semibold">{t('profile.title')}</h3>
          <p className="mt-2 text-sm text-gray-600">{t('profile.description')}</p>
        </Link>
        <Link to="/support" className="rounded-lg border border-gray-200 bg-white p-6 hover:border-gray-300">
          <h3 className="text-lg font-semibold">{t('support.title')}</h3>
          <p className="mt-2 text-sm text-gray-600">{t('support.description')}</p>
        </Link>
      </div>

      {showCheckoutSuccess && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-w-md rounded-xl bg-white p-8 text-center shadow-xl">
            <div className="text-5xl" aria-hidden="true">🎉</div>
            <h2 className="mt-4 text-2xl font-bold">{t('checkout.title')}</h2>
            <p className="mt-2 text-gray-600">{t('checkout.message', { plan: planDetails.name })}</p>
            <button
              type="button"
              onClick={dismissCheckout}
              className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
            >
              {t('checkout.cta')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
